import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Button,
  IconButton,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Divider,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  TextField,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Groups,
  Comment,
  Person,
  Visibility,
  Refresh,
  ArrowBack,
  FilterList,
  Clear,
  Today,
  Event,
  CalendarToday,
  ViewList,
  ArrowForward,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { url } from "../constants/url";

const TeamCollaboration = ({ userData }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [allCollaborations, setAllCollaborations] = useState([]);
  const [filteredCollaborations, setFilteredCollaborations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ open: false, severity: "success", message: "" });

  // Filter states
  const [dateFilter, setDateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [tempFilters, setTempFilters] = useState({
    status: "all",
  });

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const UID = userData?._id;
  const primaryColor = theme.palette.primary.main;

  useEffect(() => {
    fetchCollaborations();
  }, []);

  // Apply filters whenever filter states change
  useEffect(() => {
    applyFilters();
  }, [allCollaborations, dateFilter, statusFilter, selectedDate]);

  const fetchCollaborations = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `${url}/api/team-collaboration/user-collaborations?userId=${UID}&sortBy=lastActivity&sortOrder=desc&limit=100`
      );
      const data = await response.json();

      if (data.success) {
        setAllCollaborations(data.data.collaborations || []);
      } else {
        throw new Error(data.message || "Failed to fetch collaborations");
      }
    } catch (error) {
      console.error("❌ Error fetching collaborations:", error);
      showAlert("error", "Failed to fetch team collaborations");
    } finally {
      setLoading(false);
    }
  };

  // Apply all filters to the data
  const applyFilters = () => {
    let filtered = [...allCollaborations];

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(collaboration => 
        collaboration.leadId.status === statusFilter
      );
    }

    // Apply date filter
    if (dateFilter !== "all") {
      filtered = filterCollaborationsByDate(filtered, dateFilter, selectedDate);
    }

    setFilteredCollaborations(filtered);
  };

  // Date filtering function
  const filterCollaborationsByDate = (collaborations, dateFilter, selectedDate) => {
    const today = new Date();
    let targetDate = today;

    switch (dateFilter) {
      case "yesterday":
        targetDate = new Date(today);
        targetDate.setDate(targetDate.getDate() - 1);
        break;
      case "tomorrow":
        targetDate = new Date(today);
        targetDate.setDate(targetDate.getDate() + 1);
        break;
      case "custom":
        if (selectedDate) {
          targetDate = new Date(selectedDate);
        }
        break;
      case "today":
        targetDate = today;
        break;
      case "all":
      default:
        return collaborations;
    }

    const targetDayStart = new Date(targetDate);
    targetDayStart.setHours(0, 0, 0, 0);
    const targetDayEnd = new Date(targetDate);
    targetDayEnd.setHours(23, 59, 59, 999);

    return collaborations.filter(collaboration => {
      const collaborationDate = new Date(collaboration.lastActivity || collaboration.createdAt);
      return collaborationDate >= targetDayStart && collaborationDate <= targetDayEnd;
    });
  };

  const showAlert = (severity, message) => {
    setAlert({ open: true, severity, message });
    setTimeout(() => setAlert({ ...alert, open: false }), 6000);
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
  };

  const clearFilters = () => {
    setDateFilter("all");
    setStatusFilter("all");
    setSelectedDate(new Date().toISOString().split("T")[0]);
  };

  // Filter popover handlers
  const handleOpenFilterPopover = (event) => {
    setTempFilters({
      status: statusFilter,
    });
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const handleApplyFilters = () => {
    setStatusFilter(tempFilters.status);
    handleCloseFilterPopover();
  };

  const handleResetFilters = () => {
    setTempFilters({
      status: "all",
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "hot": return "error";
      case "warm": return "warning";
      case "cold": return "info";
      case "closed": return "success";
      case "dead": return "default";
      case "quotation_shared": return "secondary";
      case "quotation_approved": return "success";
      case "meeting_scheduled": return "info";
      case "payment_overdue": return "error";
      case "negotiation": return "warning";
      case "follow-up": return "info";
      case "rescheduled": return "secondary";
      case "quotation_rejected": return "error";
      case "quotation_expired": return "warning";
      case "token_received": return "success";
      case "partially_paid": return "info";
      case "fully_paid": return "success";
      case "quotation_revised": return "secondary";
      default: return "default";
    }
  };

const handleViewCollaboration = (collaborationId, leadId, viewType = 'full') => {
  if (viewType === 'chat') {
    // Comment icon click - only chat
    navigate(`/team-collaboration/${collaborationId}?leadId=${leadId}&view=chat`);
  } else {
    // View button click - full view with activity timeline
    navigate(`/team-collaboration/${collaborationId}?leadId=${leadId}&view=full`);
  }
};
  // Status display mapping
  const statusDisplayMap = {
    hot: "Hot",
    warm: "Warm", 
    closed: "Closed",
    dead: "Dead",
    rescheduled: "Rescheduled",
    quotation_shared: "Quotation Shared",
    quotation_approved: "Quotation Approved",
    meeting_scheduled: "Meeting Scheduled",
     quotation_rejected: "Quotation Rejected",
    quotation_expired: "Quotation Expired",
    call_not_received: "Not Received",
    "follow-up": "Follow Up",
    send_quotation: "Send Quotation",
    received: "Received",
    switch_off: "Switch Off",
    wrong_number: "Wrong Number",
    token_received: "Token Received",
    partially_paid: "Partially Paid",
    fully_paid: 'Fully Paid',
   
    negotiation: "Negotiation",
    approved: "Approved",
    rejected: "Rejected",
    pending: "Pending",
    sent: "Sent",
    overdue: "Overdue Payment",
    payment_overdue: "Payment Overdue",
    quotation_revised: "Quotation Revised",
  };

  // Status options for filter - COMPLETE LIST FROM LEADS SCREEN
  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "hot", label: "Hot Leads" },
    { value: "warm", label: "Warm Leads" },
    { value: "closed", label: "Closed Leads" },
    { value: "dead", label: "Dead Leads" },
    { value: "meeting_scheduled", label: "Meeting Scheduled" },
    { value: "quotation_shared", label: "Quotation Shared" },
    { value: "quotation_approved", label: "Quotation Approved" },
    { value: "quotation_revised", label: "Quotation Revised" },
        { value: "quotation_rejected", label: "Quotation Rejected" },
    { value: "quotation_expired", label: "Quotation Expired" },
    { value: "payment_overdue", label: "Payment Overdue" },
    { value: "negotiation", label: "Negotiation" },
    { value: "follow-up", label: "Follow Up" },
    { value: "rescheduled", label: "Rescheduled" },

    { value: "token_received", label: "Token Received" },
    { value: "partially_paid", label: "Partially Paid" },
    { value: "fully_paid", label: "Fully Paid" },
  ];

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      
      bgcolor: "background.default", 
      minHeight: "100vh",
      maxWidth: '100%',
      overflowX: 'hidden',
      mt: -3,
      ml:-2
    }}>
      {/* Header with Filters */}
      <Box sx={{ mb: 2 }}>
        {/* Top Row - Filter Button and Active Filters */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          mb: 2,
          gap: 2
        }}>
          {/* Left Side - Filter Button and Active Filters */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            {/* Filter Button */}
            <Button
              variant="contained"
              startIcon={<FilterList />}
              onClick={handleOpenFilterPopover}
              sx={{
                bgcolor: "transparent",
                boxShadow: "none",
                border: "none",
                color: primaryColor,
                "&:hover": {
                  bgcolor: alpha(primaryColor, 0.08),
                  boxShadow: "none",
                  border: "none",
                },
                minWidth: "auto",
                px: 2.3,
                height: "38px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "15px",
              }}
            >
              FILTERS
            </Button>

            {/* Active Filters Chips */}
            {(statusFilter !== "all" || dateFilter !== "all") && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {/* Date Filter Chip */}
                {dateFilter !== "all" && (
                  <Chip
                    icon={
                      dateFilter === "custom" ? (
                        <Event />
                      ) : dateFilter === "today" ? (
                        <Today />
                      ) : dateFilter === "yesterday" ? (
                        <ArrowBack />
                      ) : dateFilter === "tomorrow" ? (
                        <ArrowForward />
                      ) : (
                        <ViewList />
                      )
                    }
                    label={
                      dateFilter === "custom"
                        ? `Custom: ${selectedDate}`
                        : dateFilter === "today"
                        ? "Today"
                        : dateFilter === "yesterday"
                        ? "Yesterday"
                        : dateFilter === "tomorrow"
                        ? "Tomorrow"
                        : "All"
                    }
                    size="medium"
                    onDelete={() => handleDateFilterChange("all")}
                    sx={{
                      color: "success.main",
                      fontWeight: 700,
                      height: "32px",
                    }}
                  />
                )}

                {/* Status Filter Chip */}
                {statusFilter !== "all" && (
                  <Chip
                    label={`Status: ${statusDisplayMap[statusFilter] || statusFilter}`}
                    size="medium"
                    onDelete={() => handleStatusFilterChange("all")}
                    sx={{
                      color: "success.main",
                      fontWeight: 700,
                      height: "32px",
                    }}
                  />
                )}

                {/* Clear All Filters */}
                <Button
                  size="small"
                  startIcon={<Clear />}
                  onClick={clearFilters}
                  sx={{
                    color: "error.main",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                    height: "32px",
                  }}
                >
                  Clear All
                </Button>
              </Box>
            )}
          </Box>

          {/* Refresh Button */}
          {/* <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchCollaborations}
            size="small"
          >
            Refresh
          </Button> */}
        </Box>

        {/* Second Row - Stats and Date Filters */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          flexWrap: "wrap", 
          gap: 1 
        }}>
          {/* Left Side - Collaboration Statistics */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Chip
              label={`Total: ${filteredCollaborations.length}`}
              variant="outlined"
              color="primary"
              size="medium"
              sx={{ fontWeight: "bold", height: "32px" }}
            />
            <Chip
              label={`Comments: ${filteredCollaborations.reduce((acc, collab) => acc + collab.commentCount, 0)}`}
              variant="filled"
              color="secondary"
              size="medium"
              sx={{ fontWeight: "bold", height: "32px" }}
            />
            {/* <Chip
              label={`Participants: ${filteredCollaborations.reduce((acc, collab) => acc + collab.participants.length, 0)}`}
              variant="filled"
              color="info"
              size="medium"
              sx={{ fontWeight: "bold", height: "32px" }}
            /> */}
          </Box>

          {/* Right Side - Date Filters */}
          <Box sx={{ 
            display: "flex", 
            gap: 1, 
            flexWrap: "wrap", 
            alignItems: "center" 
          }}>
            {[
              { value: "all", label: "All", icon: <ViewList /> },
              { value: "yesterday", label: "Yesterday", icon: <ArrowBack /> },
              { value: "today", label: "Today", icon: <Today /> },
              { value: "tomorrow", label: "Tomorrow", icon: <ArrowForward /> },
            ].map((filter) => (
              <Chip
                key={filter.value}
                icon={filter.icon}
                label={filter.label}
                variant={dateFilter === filter.value ? "filled" : "outlined"}
                color={dateFilter === filter.value ? "primary" : "default"}
                onClick={() => handleDateFilterChange(filter.value)}
                size="medium"
                sx={{
                  height: "32px",
                  ...(dateFilter === filter.value && {
                    bgcolor: "primary.main",
                    "&:hover": { bgcolor: alpha(primaryColor, 0.9) },
                  }),
                }}
              />
            ))}

            {/* Date Picker */}
            <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
              <TextField
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setDateFilter("custom");
                }}
                sx={{
                  width: "130px",
                  "& .MuiInputBase-root": {
                    height: 40,
                    borderRadius: 1,
                    border: `1px solid ${alpha(primaryColor, 0.3)}`,
                    "&:hover": {
                      borderColor: primaryColor,
                    },
                    "&.Mui-focused": {
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 2px ${alpha(primaryColor, 0.2)}`,
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 12px",
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Alert */}
      {alert.open && (
        <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, open: false })}>
          {alert.message}
        </Alert>
      )}

      {/* Collaborations Table */}
      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: 3,
        overflow: 'hidden',
        height: 'fit-content',
      }}>
        <CardContent sx={{ p: 0 }}>
          {filteredCollaborations.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Groups sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {statusFilter !== "all" || dateFilter !== "all" ? "No matching collaborations" : "No Team Collaborations Yet"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {statusFilter !== "all" || dateFilter !== "all" 
                  ? "Try adjusting your filters to see more results." 
                  : "When you or your team members add someone to collaborate on a lead, it will appear here."}
              </Typography>
              {(statusFilter !== "all" || dateFilter !== "all") && (
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{ mr: 2 }}
                >
                  Clear Filters
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<Groups />}
                onClick={() => navigate("/leads")}
              >
                Go to Leads to Start Collaborating
              </Button>
            </Box>
          ) : (
            <TableContainer 
              component={Paper} 
              sx={{ 
                boxShadow: 'none',
                maxHeight: '70vh'
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                      Client Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                      Contact No
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                      Services
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                      Participants
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                      Comments
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                      Last Activity
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
          <TableBody>
  {filteredCollaborations.map((collaboration) => (
    <TableRow
      key={collaboration._id}
      sx={{
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
        },
        borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`,
        "&:last-child td": {
          borderBottom: "none",
        },
      }}
    >
      <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {collaboration.leadId.clientName}
        </Typography>
      </TableCell>
      <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
        {collaboration.leadId.contactNo}
      </TableCell>
      <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
        {collaboration.leadId.serviceInterested?.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {collaboration.leadId.serviceInterested.slice(0, 2).map((service, index) => (
              <Chip
                key={index}
                label={service.service}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))}
            {collaboration.leadId.serviceInterested.length > 2 && (
              <Chip
                label={`+${collaboration.leadId.serviceInterested.length - 2}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            No services
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
        <Chip
          label={statusDisplayMap[collaboration.leadId.status] || collaboration.leadId.status}
          size="small"
          color={getStatusColor(collaboration.leadId.status)}
          sx={{ fontWeight: "bold" }}
        />
      </TableCell>
      <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {collaboration.participants.slice(0, 2).map((participant) => (
            <Chip
              key={participant.userId._id}
              label={participant.userId.name}
              size="small"
              variant="outlined"
            />
          ))}
          {collaboration.participants.length > 2 && (
            <Chip
              label={`+${collaboration.participants.length - 2}`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
        <IconButton
          size="small"
          onClick={() =>
            handleViewCollaboration(
              collaboration._id,
              collaboration.leadId._id,
              "chat"
            )
          }
          sx={{
            color: "primary.main",
            "&:hover": {
              backgroundColor: alpha(primaryColor, 0.1),
            },
          }}
          title="Open Chat"
        >
          <Comment />
        </IconButton>
      </TableCell>
      
      {/* ADD THIS MISSING LAST ACTIVITY COLUMN */}
      <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
        <Typography variant="body2">
          {formatDate(collaboration.lastActivity)}
        </Typography>
      </TableCell>

      <TableCell>
        <Button
          variant="contained"
          size="small"
          startIcon={<Visibility />}
          onClick={() =>
            handleViewCollaboration(
              collaboration._id,
              collaboration.leadId._id,
              "full"
            )
          }
          sx={{ borderRadius: 1 }}
        >
          View
        </Button>
      </TableCell>
    </TableRow>
  ))}
</TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilterPopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 250,
            borderRadius: 2,
            border: `2px solid ${alpha(primaryColor, 0.1)}`,
            bgcolor: "background.paper",
          },
        }}
      >
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Status Filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={tempFilters.status}
              label="Status"
              onChange={(e) =>
                setTempFilters({ ...tempFilters, status: e.target.value })
              }
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}
          >
            <Button onClick={handleResetFilters} color="inherit">
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{
                bgcolor: "primary.main",
                "&:hover": {
                  bgcolor: alpha(primaryColor, 0.9),
                },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default TeamCollaboration;